import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Trophy, CaretRight, CaretLeft, Check, MapPin, Users, UsersThree,
  UserCirclePlus, Scales, CalendarBlank, Clock, Warning, Crown, Sword,
  Megaphone, SortAscending
} from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { WizardStepVenue, WizardStepPlayers, WizardStepOrganizers, WizardStepReview } from "@/components/wizard/WizardSteps";
import { promptSuperPassword } from "@/components/admin/SuperPasswordDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const STEPS = [
  { label: "Details", icon: Trophy },
  { label: "Venue", icon: MapPin },
  { label: "Players", icon: Users },
  { label: "Organizers", icon: UserCirclePlus },
  { label: "Review", icon: Check },
];

export default function TournamentWizard({ onComplete, onCancel, editTournament }) {
  const isEditing = !!editTournament;
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [venues, setVenues] = useState([]);
  const [players, setPlayers] = useState([]);
  const [externalPlayers, setExternalPlayers] = useState([]);

  // Step 1: Details
  const [form, setForm] = useState({
    name: editTournament?.name || "",
    slug: editTournament?.slug || "",
    tournament_type: editTournament?.tournament_type || "monthly",
    tournament_format: editTournament?.tournament_format || "knockout",
    date: editTournament?.date || "",
    end_date: editTournament?.end_date || "",
    description: editTournament?.description || "",
    youtube_live_url: editTournament?.youtube_live_url || "",
    best_of: editTournament?.best_of || 5,
    num_groups: editTournament?.num_groups || 0,
    advance_count: editTournament?.advance_count || 2,
    bye_strategy: editTournament?.bye_strategy || "best_thirds",
    is_internal: editTournament?.is_internal ?? true,
    min_players: editTournament?.min_players || 4,
    registration_open: editTournament?.registration_open || false,
    announcement: editTournament?.announcement || "",
    stages_group: editTournament?.stages_best_of?.group_stage?.toString() || "",
    stages_r16: editTournament?.stages_best_of?.round_16?.toString() || "",
    stages_qf: editTournament?.stages_best_of?.quarter_final?.toString() || "",
    stages_sf: editTournament?.stages_best_of?.semi_final?.toString() || "",
    stages_final: editTournament?.stages_best_of?.final?.toString() || "",
    year: editTournament?.year || "",
    season_id: editTournament?.season_id || "",
    scoring_mode: editTournament?.scoring_mode || "self",
    scoring_type: editTournament?.scoring_type || "submission",
  });

  // Step 2: Venue (multi-select)
  const [selectedVenueIds, setSelectedVenueIds] = useState(editTournament?.venue_ids || []);

  // Step 3: Players
  const [selectedInternal, setSelectedInternal] = useState([]);
  const [selectedExternal, setSelectedExternal] = useState([]);
  const [playerSearch, setPlayerSearch] = useState("");

  // Step 4: Organizers
  const [selectedOrganizers, setSelectedOrganizers] = useState(editTournament?.organizer_player_ids || []);

  // Seasons
  const [seasons, setSeasons] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [v, p, ep, s] = await Promise.all([
          axios.get(`${API}/venues`),
          axios.get(`${API}/players?include_external=false`),
          axios.get(`${API}/players/external`),
          axios.get(`${API}/settings/seasons`),
        ]);
        setVenues(v.data);
        setPlayers(p.data);
        setExternalPlayers(ep.data);
        setSeasons(s.data || []);
        
        // Pre-select players if editing
        if (editTournament?.registered_players) {
          const allPIds = new Set(editTournament.registered_players);
          const internalIds = v.data.length ? p.data.filter(pl => allPIds.has(pl.id)).map(pl => pl.id) : [];
          const externalIds = ep.data.filter(pl => allPIds.has(pl.id)).map(pl => pl.id);
          setSelectedInternal(internalIds);
          setSelectedExternal(externalIds);
        }
      } catch (err) {
        console.error("Failed to fetch wizard data:", err);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalSelected = selectedInternal.length + selectedExternal.length;

  const canProceed = () => {
    if (step === 0) return form.name && form.date && form.tournament_type;
    if (step === 1) return true;
    if (step === 2) return totalSelected >= form.min_players;
    if (step === 3) return true;
    return true;
  };

  const handleNext = () => { if (step < STEPS.length - 1) setStep(step + 1); };
  const handleBack = () => { if (step > 0) setStep(step - 1); };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const stages_best_of = {};
      if (form.stages_group) stages_best_of.group_stage = parseInt(form.stages_group);
      if (form.stages_r16) stages_best_of.round_16 = parseInt(form.stages_r16);
      if (form.stages_qf) stages_best_of.quarter_final = parseInt(form.stages_qf);
      if (form.stages_sf) stages_best_of.semi_final = parseInt(form.stages_sf);
      if (form.stages_final) stages_best_of.final = parseInt(form.stages_final);

      const payload = {
        name: form.name,
        slug: form.slug?.trim() || undefined,
        tournament_type: form.tournament_type,
        tournament_format: form.tournament_format, date: form.date,
        end_date: form.end_date || null, description: form.description || null,
        venue_ids: selectedVenueIds, youtube_live_url: form.youtube_live_url || null,
        best_of: form.best_of,
        year: form.year || null, season_id: form.season_id || null,
        stages_best_of: Object.keys(stages_best_of).length > 0 ? stages_best_of : null,
        num_groups: form.tournament_format === "groups_knockout" ? (form.num_groups || null) : null,
        advance_count: form.tournament_format === "groups_knockout" ? form.advance_count : null,
        bye_strategy: form.tournament_format === "groups_knockout" ? form.bye_strategy : null,
        internal_player_ids: selectedInternal, external_player_ids: selectedExternal,
        organizer_player_ids: selectedOrganizers,
        is_internal: form.is_internal, min_players: form.min_players,
        registration_open: form.registration_open,
        announcement: form.announcement || null,
        scoring_mode: form.scoring_mode || "self",
        scoring_type: form.scoring_type || "submission",
      };
      let res;
      if (isEditing) {
        const pw = await promptSuperPassword({
          title: "Re-run tournament setup?",
          description: "This deletes the existing tournament's matches and rebuilds them from this configuration. Only allowed when no matches have been completed.",
          actionLabel: "Re-setup Tournament",
        });
        if (!pw) { setSubmitting(false); return; }
        res = await axios.put(`${API}/tournaments/${editTournament.id}/re-setup`, payload, { headers: { "X-Super-Password": pw }, withCredentials: true });
        toast.success(`Tournament "${form.name}" updated with ${res.data.player_count} players!`);
        onComplete?.(editTournament.id);
      } else {
        res = await axios.post(`${API}/tournaments/setup`, payload, { withCredentials: true });
        toast.success(`Tournament "${form.name}" created with ${res.data.player_count} players!`);
        onComplete?.(res.data.tournament_id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create tournament");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleVenue = (id) => {
    setSelectedVenueIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const togglePlayer = (id, list, setList) => {
    setList(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAllInternal = () => setSelectedInternal(players.map(p => p.id));
  const deselectAllInternal = () => setSelectedInternal([]);
  const selectByRanking = (topN) => {
    const sorted = [...players].sort((a, b) => (a.ranking || 999) - (b.ranking || 999));
    setSelectedInternal(sorted.slice(0, topN).map(p => p.id));
  };

  const filteredPlayers = players.filter(p =>
    p.name.toLowerCase().includes(playerSearch.toLowerCase())
  );
  const filteredExternal = externalPlayers.filter(p =>
    p.name.toLowerCase().includes(playerSearch.toLowerCase())
  );

  const hasConflict = (player) => {
    if (!form.date || !player.unavailable_dates) return false;
    return player.unavailable_dates.includes(form.date);
  };

  const getVenueAvailability = (venue) => {
    if (!venue?.availability?.length || !form.date) return null;
    try {
      const d = new Date(form.date);
      const dayOfWeek = (d.getDay() + 6) % 7;
      return venue.availability.find(a => a.day_of_week === dayOfWeek);
    } catch { return null; }
  };

  return (
    <div className="space-y-6" data-testid="tournament-wizard">
      {/* Step Indicator */}
      <div className="flex items-center justify-between px-2">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isActive = i === step;
          const isDone = i < step;
          return (
            <div key={`step-${s.label}`} className="flex items-center flex-1 last:flex-none">
              <button
                onClick={() => i < step && setStep(i)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm ${
                  isActive ? "bg-primary/20 text-primary font-semibold" :
                  isDone ? "text-primary/70 cursor-pointer hover:bg-primary/10" :
                  "text-muted-foreground"
                }`}
                data-testid={`wizard-step-${i}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  isActive ? "bg-primary text-primary-foreground" :
                  isDone ? "bg-primary/30 text-primary" : "bg-muted text-muted-foreground"
                }`}>
                  {isDone ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className="hidden md:inline">{s.label}</span>
              </button>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-px mx-2 ${isDone ? "bg-primary/40" : "bg-border"}`} />
              )}
            </div>
          );
        })}
      </div>

      <Card className="bg-card border-border">
        <CardContent className="pt-6">
          {/* ============ STEP 0: DETAILS ============ */}
          {step === 0 && (
            <div className="space-y-5" data-testid="wizard-step-details">
              <div className="space-y-2">
                <Label>Tournament Name *</Label>
                <Input value={form.name} placeholder="e.g. February Monthly Knockout"
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className="bg-background border-border" data-testid="tournament-name-input" />
              </div>
              <div className="space-y-2">
                <Label>URL slug <span className="text-muted-foreground text-xs font-normal">(optional — auto-generated from name)</span></Label>
                <Input
                  value={form.slug}
                  placeholder="e.g. k-cup-2026 → /tournaments/k-cup-2026"
                  onChange={e => setForm({ ...form, slug: e.target.value })}
                  className="bg-background border-border font-mono text-sm"
                  data-testid="tournament-slug-input"
                />
                {(form.slug || form.name) && (
                  <p className="text-[10px] text-muted-foreground">
                    URL: /tournaments/<span className="text-secondary">{(form.slug || form.name).toString().toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 60) || "auto"}</span>
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={form.tournament_type} onValueChange={v => setForm({ ...form, tournament_type: v })}>
                    <SelectTrigger className="bg-background border-border" data-testid="tournament-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="major">Major (x1.0)</SelectItem>
                      <SelectItem value="monthly">Monthly (x0.5)</SelectItem>
                      <SelectItem value="weekly">Weekly (x0.25)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Format *</Label>
                  <Select value={form.tournament_format} onValueChange={v => setForm({ ...form, tournament_format: v })}>
                    <SelectTrigger className="bg-background border-border" data-testid="tournament-format-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="knockout">Knockout</SelectItem>
                      <SelectItem value="groups_knockout">Groups + Knockout</SelectItem>
                      <SelectItem value="league">League (Round Robin)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Start Date *</Label>
                  <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                    className="bg-background border-border" data-testid="tournament-date-input" />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })}
                    className="bg-background border-border" />
                </div>
                <div className="space-y-2">
                  <Label>Default Best Of</Label>
                  <Select value={String(form.best_of)} onValueChange={v => setForm({ ...form, best_of: parseInt(v) })}>
                    <SelectTrigger className="bg-background border-border" data-testid="tournament-bestof-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2,3,5,7,9].map(n => <SelectItem key={n} value={String(n)}>Best of {n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Input value={form.year} placeholder="e.g. 2026"
                    onChange={e => setForm({ ...form, year: e.target.value })}
                    className="bg-background border-border" data-testid="tournament-year-input" />
                </div>
                <div className="space-y-2">
                  <Label>Season</Label>
                  <Select value={form.season_id} onValueChange={v => setForm({ ...form, season_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="bg-background border-border" data-testid="tournament-season-select"><SelectValue placeholder="Select season" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Season</SelectItem>
                      {seasons.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.start_date} - {s.end_date})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Stage-specific Best Of overrides */}
              <div className="p-4 rounded-lg bg-muted/20 border border-border space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Frames per Stage (override default)</Label>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { key: "stages_group", label: "Groups" },
                    { key: "stages_r16", label: "R16" },
                    { key: "stages_qf", label: "QF" },
                    { key: "stages_sf", label: "SF" },
                    { key: "stages_final", label: "Final" },
                  ].map(s => (
                    <div key={s.key} className="space-y-1">
                      <Label className="text-[11px]">{s.label}</Label>
                      <Select value={form[s.key] || ""} onValueChange={v => setForm({ ...form, [s.key]: v === "default" ? "" : v })}>
                        <SelectTrigger className="bg-background border-border h-8 text-xs"><SelectValue placeholder="Def" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Default</SelectItem>
                          {[2,3,5,7,9].map(n => <SelectItem key={n} value={String(n)}>Bo{n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              {form.tournament_format === "groups_knockout" && (
                <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-muted/20 border border-border">
                  <div className="space-y-2">
                    <Label>Number of Groups (0 = auto)</Label>
                    <Input type="number" min="0" max="8" value={form.num_groups}
                      onChange={e => setForm({ ...form, num_groups: parseInt(e.target.value) || 0 })}
                      className="bg-background border-border" data-testid="tournament-numgroups-input" />
                    <p className="text-[11px] text-muted-foreground">Leave at 0 to auto-calculate based on player count</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Advance per Group</Label>
                    <Input type="number" min="1" max="4" value={form.advance_count}
                      onChange={e => setForm({ ...form, advance_count: parseInt(e.target.value) || 2 })}
                      className="bg-background border-border" data-testid="tournament-advance-input" />
                    <p className="text-[11px] text-muted-foreground">Top N from each group qualify for knockouts</p>
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>If bracket has empty slots…</Label>
                    <select
                      value={form.bye_strategy}
                      onChange={e => setForm({ ...form, bye_strategy: e.target.value })}
                      className="w-full h-10 bg-background border border-border rounded px-3 text-sm"
                      data-testid="tournament-bye-strategy-select"
                    >
                      <option value="best_thirds">Fill with best Nth-place finishers (system computes how many)</option>
                      <option value="bye">Leave as BYE (auto-advance)</option>
                    </select>
                    <p className="text-[11px] text-muted-foreground">
                      Used when group qualifiers don't fill a power-of-2 bracket — e.g., 3 groups × top 2 = 6 → bracket of 8 has 2 empty slots.
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border">
                  <Switch checked={form.is_internal} onCheckedChange={v => setForm({ ...form, is_internal: v })} data-testid="internal-toggle" />
                  <div>
                    <Label className="font-medium">Internal Only</Label>
                    <p className="text-xs text-muted-foreground">Annadil Burhani DSM members only</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border">
                  <Switch checked={form.registration_open} onCheckedChange={v => setForm({ ...form, registration_open: v })} data-testid="registration-toggle" />
                  <div>
                    <Label className="font-medium">Open Registration</Label>
                    <p className="text-xs text-muted-foreground">Players can self-register</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Min Players</Label>
                <Input type="number" min="2" max="64" value={form.min_players}
                  onChange={e => setForm({ ...form, min_players: parseInt(e.target.value) || 2 })}
                  className="bg-background border-border w-24" />
              </div>

              <div className="space-y-2">
                <Label>Scoring Mode</Label>
                <Select value={form.scoring_mode} onValueChange={v => setForm({ ...form, scoring_mode: v })}>
                  <SelectTrigger className="bg-background border-border w-full md:w-96" data-testid="wizard-scoring-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Self-scoring (either player can score their match)</SelectItem>
                    <SelectItem value="referee_only">Referee-only (admin or assigned referee)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {form.scoring_mode === "self"
                    ? "Players with claimed profiles can update their own match score. Admin can override per match during play."
                    : "Only admin or the assigned referee can update scores. Use for official/competitive events."}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Scoring Type</Label>
                <Select value={form.scoring_type} onValueChange={v => setForm({ ...form, scoring_type: v })}>
                  <SelectTrigger className="bg-background border-border w-full md:w-96" data-testid="wizard-scoring-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="submission">Submission (enter final frames after match)</SelectItem>
                    <SelectItem value="live">Live scoring (full frame + shot tracking)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {form.scoring_type === "live"
                    ? "Frame-by-frame live panel: shots, fouls, breaks tracked in real-time. Best with a referee."
                    : "Players/admin enter final frames after the match (with optional high break). Faster for casual club play."}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Megaphone className="w-4 h-4" />Tournament Announcement</Label>
                <Textarea value={form.announcement} placeholder="Optional: Announce this tournament to club members..."
                  onChange={e => setForm({ ...form, announcement: e.target.value })}
                  className="bg-background border-border" />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={form.description} placeholder="Optional tournament description..."
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  className="bg-background border-border" />
              </div>
            </div>
          )}

          {/* ============ STEP 1: VENUE ============ */}
          {step === 1 && (
            <WizardStepVenue venues={venues} selectedVenueIds={selectedVenueIds} toggleVenue={toggleVenue} form={form} getVenueAvailability={getVenueAvailability} />
          )}

          {/* ============ STEP 2: PLAYERS ============ */}
          {step === 2 && (
            <WizardStepPlayers
              players={players} externalPlayers={externalPlayers}
              selectedInternal={selectedInternal} selectedExternal={selectedExternal}
              setSelectedInternal={setSelectedInternal} setSelectedExternal={setSelectedExternal}
              playerSearch={playerSearch} setPlayerSearch={setPlayerSearch}
              form={form} selectAllInternal={selectAllInternal} deselectAllInternal={deselectAllInternal}
              selectByRanking={selectByRanking} totalSelected={totalSelected}
              filteredPlayers={filteredPlayers} hasConflict={hasConflict} togglePlayer={togglePlayer}
            />
          )}

          {/* ============ STEP 3: ORGANIZERS ============ */}
          {step === 3 && (
            <WizardStepOrganizers players={players} selectedOrganizers={selectedOrganizers} togglePlayer={togglePlayer} setSelectedOrganizers={setSelectedOrganizers} />
          )}

          {/* ============ STEP 4: REVIEW ============ */}
          {step === 4 && (
            <WizardStepReview form={form} selectedVenueIds={selectedVenueIds} venues={venues} totalSelected={totalSelected} selectedOrganizers={selectedOrganizers} players={players} />
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={step === 0 ? onCancel : handleBack} className="border-border" data-testid="wizard-back-btn">
          <CaretLeft className="w-4 h-4 mr-1" />{step === 0 ? "Cancel" : "Back"}
        </Button>
        <div className="text-sm text-muted-foreground">Step {step + 1} of {STEPS.length}</div>
        {step < STEPS.length - 1 ? (
          <Button onClick={handleNext} disabled={!canProceed()} data-testid="wizard-next-btn">
            Next <CaretRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={submitting || !canProceed()} className="bg-primary hover:bg-primary/90" data-testid="wizard-create-btn">
            {submitting ? (isEditing ? "Updating..." : "Creating...") : (isEditing ? "Update Tournament" : "Create Tournament")} <Trophy className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
